/* ============================================================
   Hero Ticker - Scoped JS
   Infinite, draggable, free-flow ticker carousel.
   requestAnimationFrame + translateX, Pointer Events for drag.

   Three-level responsive:
     >= 1200px  -> static row   (ticker off)
     769-1199   -> ticker       (animation + drag)
     <= 768px   -> static list  (ticker off)

   Resize resilience:
     - Clears transform before measuring scrollWidth (DevTools safe)
     - rAF after debounce waits for emulation layout to commit
     - Tracks last mode: full state reset on every breakpoint cross
     - orientationchange fires same handler as resize
     - IntersectionObserver restart gated on tickerActive
   ============================================================ */
(() => {
  const DRAG_THRESHOLD    = 5;     // px  - click vs drag dead-zone
  const RESIZE_DEBOUNCE   = 150;   // ms  - debounce before remeasure
  const STATIC_BREAKPOINT = 1200;  // px  - >= this: static desktop row
  const MOBILE_BREAKPOINT = 768;   // px  - <= this: static mobile list
  const instances = new Map();     // sectionId -> destroy()

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isStaticMode = () =>
    window.innerWidth >= STATIC_BREAKPOINT || window.innerWidth <= MOBILE_BREAKPOINT;

  /* ================================================================
     initTicker — controls a single .hero-ticker section
     ================================================================ */
  function initTicker(section) {
    const sectionId = section.id;
    // Tear down any existing instance before re-init (Theme Editor, etc.)
    if (instances.has(sectionId)) instances.get(sectionId)();

    const carousel = section.querySelector('.hero-ticker__carousel');
    const track    = section.querySelector('.hero-ticker__track');
    if (!carousel || !track) return;

    const speed     = parseFloat(section.dataset.speed) || 0.3;
    const direction = section.dataset.direction === 'right' ? 1 : -1;

    // --- Mutable state ---
    let position     = 0;
    let halfWidth    = 0;
    let isPaused     = false;
    let isDragging   = false;
    let animFrameId  = null;
    let tickerActive = false;
    let lastStatic   = null;   // undefined = never checked
    let intersecting = true;   // assume visible until observer says otherwise

    // Drag scratch
    let dragStartX   = 0;
    let dragStartPos = 0;
    let dragDelta    = 0;
    let didDrag      = false;

    /* -------------------------------------------------------
       measure
       Always clears the inline transform before reading
       scrollWidth so DevTools emulation cannot return a
       stale or transform-shifted value.
       Caller is responsible for restoring the transform.
       ------------------------------------------------------- */
    function measure() {
      track.style.transform = '';
      void track.scrollWidth; // flush pending style recalc
      halfWidth = track.scrollWidth / 2;
    }

    /* -------------------------------------------------------
       Animation loop
       ------------------------------------------------------- */
    function animate() {
      if (tickerActive && !isPaused && !isDragging && !prefersReducedMotion()) {
        position += direction * speed;
        wrapPosition();
        track.style.transform = 'translateX(' + position + 'px)';
      }
      animFrameId = requestAnimationFrame(animate);
    }

    function wrapPosition() {
      if (halfWidth === 0) return;
      if (direction === -1 && position <= -halfWidth) position += halfWidth;
      else if (direction === 1 && position >= 0)      position -= halfWidth;
      // Guard runaway values after mode switches
      if (Math.abs(position) > halfWidth * 2) position = position % halfWidth;
    }

    /* -------------------------------------------------------
       enableTicker
       Always measures fresh — no early-return guard.
       This ensures every checkBreakpoint() call (whether on
       first enable or after a DevTools emulation swap) gets
       accurate track dimensions.
       ------------------------------------------------------- */
    function enableTicker() {
      // Cancel any leftover frame before remeasuring
      if (animFrameId) {
        cancelAnimationFrame(animFrameId);
        animFrameId = null;
      }
      tickerActive = true;
      measure();
      // Reset to a clean start position
      position = (direction === -1) ? 0 : -halfWidth;
      track.style.transform = 'translateX(' + position + 'px)';
      // Only start the loop if the section is visible
      if (intersecting) animate();
    }

    /* -------------------------------------------------------
       remeasure
       Called when staying in ticker mode across a resize.
       Preserves scroll progress (proportional to new width)
       instead of jumping to position 0.
       ------------------------------------------------------- */
    function remeasure() {
      const prevHalf = halfWidth;
      measure(); // clears transform
      if (prevHalf > 0 && halfWidth > 0) {
        // Scale position proportionally to new track width
        position = (position / prevHalf) * halfWidth;
      }
      wrapPosition();
      // Restore transform at new position
      if (tickerActive) {
        track.style.transform = 'translateX(' + position + 'px)';
      }
    }

    /* -------------------------------------------------------
       disableTicker
       Full state reset — clears rAF, drag state, paused flag.
       Ensures nothing from ticker mode bleeds into static mode.
       ------------------------------------------------------- */
    function disableTicker() {
      tickerActive = false;
      isPaused     = false;
      isDragging   = false;
      dragStartX   = 0;
      dragStartPos = 0;
      dragDelta    = 0;
      didDrag      = false;
      track.classList.remove('is-dragging');
      // Cancel rAF before clearing transform so the next frame
      // cannot write a stale translateX after the clear
      if (animFrameId) {
        cancelAnimationFrame(animFrameId);
        animFrameId = null;
      }
      track.style.transform = '';
    }

    /* -------------------------------------------------------
       checkBreakpoint
       Detects mode changes (static <-> ticker).
       On a mode change: full reset via disable/enable.
       Within ticker mode: lighter remeasure (keeps position).
       ------------------------------------------------------- */
    function checkBreakpoint() {
      const nowStatic  = isStaticMode();
      const modeChanged = (nowStatic !== lastStatic);
      lastStatic = nowStatic;

      if (nowStatic) {
        disableTicker();
      } else if (modeChanged || !tickerActive) {
        // Crossed into ticker zone, or fresh init — full enable (remeasures)
        enableTicker();
      } else {
        // Staying in ticker mode — remeasure without resetting position
        remeasure();
      }
    }

    /* -------------------------------------------------------
       Hover — pause / resume
       ------------------------------------------------------- */
    function onMouseEnter() { if (tickerActive) isPaused = true; }
    function onMouseLeave() { isPaused = false; }

    /* -------------------------------------------------------
       Pointer / Drag
       ------------------------------------------------------- */
    function onPointerDown(e) {
      if (e.button !== 0 || !tickerActive) return;
      isDragging   = true;
      didDrag      = false;
      dragStartX   = e.clientX;
      dragStartPos = position;
      dragDelta    = 0;
      // Capture is intentionally deferred to onPointerMove (set only once the
      // drag threshold is crossed). Setting it here would cause click to fire
      // on the track instead of the card element, breaking link navigation
      // for clean taps in carousel mode.
      track.classList.add('is-dragging');
    }

    function onPointerMove(e) {
      if (!isDragging) return;
      dragDelta = e.clientX - dragStartX;
      if (Math.abs(dragDelta) > DRAG_THRESHOLD) {
        if (!didDrag) {
          // Capture now that drag is confirmed so pointerup is received
          // even if the pointer leaves the track element mid-drag.
          track.setPointerCapture(e.pointerId);
        }
        didDrag = true;
      }
      position = dragStartPos + dragDelta;
      wrapPosition();
      track.style.transform = 'translateX(' + position + 'px)';
    }

    function onPointerUp(e) {
      if (!isDragging) return;
      isDragging = false;
      if (track.hasPointerCapture(e.pointerId)) {
        track.releasePointerCapture(e.pointerId);
      }
      track.classList.remove('is-dragging');
      wrapPosition();
    }

    function onPointerCancel(e) { onPointerUp(e); }

    function onCardClick(e) {
      if (didDrag) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    /* -------------------------------------------------------
       IntersectionObserver — pause rAF when off-screen.
       Restart is gated on tickerActive so a static-mode
       section never accidentally starts the animation loop.
       ------------------------------------------------------- */
    let observer = null;

    function setupObserver() {
      observer = new IntersectionObserver(([entry]) => {
        intersecting = entry.isIntersecting;
        if (intersecting) {
          // Only (re)start loop if ticker should be running
          if (tickerActive && !animFrameId) animate();
        } else {
          if (animFrameId) {
            cancelAnimationFrame(animFrameId);
            animFrameId = null;
          }
        }
      }, { threshold: 0 });
      observer.observe(section);
    }

    /* -------------------------------------------------------
       Resize + orientation change handler (debounced + rAF).

       The extra requestAnimationFrame after the debounce lets
       the browser commit DevTools device emulation layout
       changes before we read scrollWidth, eliminating the
       "wrong half-width on emulated mobile" class of bugs.
       ------------------------------------------------------- */
    let resizeTimer = null;

    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // One paint cycle after layout commits (critical for DevTools emulation)
        requestAnimationFrame(() => {
          checkBreakpoint();
        });
      }, RESIZE_DEBOUNCE);
    }

    /* -------------------------------------------------------
       Event binding
       ------------------------------------------------------- */
    carousel.addEventListener('mouseenter', onMouseEnter);
    carousel.addEventListener('mouseleave', onMouseLeave);
    track.addEventListener('pointerdown',   onPointerDown);
    track.addEventListener('pointermove',   onPointerMove);
    track.addEventListener('pointerup',     onPointerUp);
    track.addEventListener('pointercancel', onPointerCancel);
    track.querySelectorAll('a.hero-ticker__card').forEach((link) => {
      link.addEventListener('click', onCardClick);
    });
    window.addEventListener('resize',            onResize);
    window.addEventListener('orientationchange', onResize);

    /* -------------------------------------------------------
       Start
       ------------------------------------------------------- */
    setupObserver();
    checkBreakpoint(); // sets initial mode

    /* -------------------------------------------------------
       Cleanup
       ------------------------------------------------------- */
    function destroy() {
      disableTicker(); // cancels rAF, clears state
      if (observer) observer.disconnect();
      clearTimeout(resizeTimer);
      carousel.removeEventListener('mouseenter', onMouseEnter);
      carousel.removeEventListener('mouseleave', onMouseLeave);
      track.removeEventListener('pointerdown',   onPointerDown);
      track.removeEventListener('pointermove',   onPointerMove);
      track.removeEventListener('pointerup',     onPointerUp);
      track.removeEventListener('pointercancel', onPointerCancel);
      track.querySelectorAll('a.hero-ticker__card').forEach((link) => {
        link.removeEventListener('click', onCardClick);
      });
      window.removeEventListener('resize',            onResize);
      window.removeEventListener('orientationchange', onResize);
      instances.delete(sectionId);
    }

    instances.set(sectionId, destroy);
  }

  /* ================================================================
     Boot
     ================================================================ */
  function initAll() {
    document.querySelectorAll('.hero-ticker').forEach(initTicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ---- Theme Editor ---- */
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.hero-ticker');
    if (section) initTicker(section);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('.hero-ticker');
    if (section && instances.has(section.id)) instances.get(section.id)();
  });

  /* ================================================================
     Word Carousel — rotates the first word of the hero heading
     ================================================================ */
  (function initWordCarousels() {
    const TRANSITION = 350; // ms — matches CSS transition duration

    function startCarousel(el) {
      const raw      = el.dataset.words || '';
      const interval = parseInt(el.dataset.interval, 10) || 2500;
      const words    = raw.split('|').map(w => w.trim()).filter(Boolean);
      if (words.length < 2) return;

      const current  = el.querySelector('.hero-ticker__word-current');
      if (!current) return;

      /* Pre-measure widths so the container never reflows */
      const probe = document.createElement('span');
      probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;font:inherit';
      el.appendChild(probe);
      let maxW = 0;
      words.forEach(w => { probe.textContent = w; maxW = Math.max(maxW, probe.offsetWidth); });
      el.removeChild(probe);
      el.style.minWidth = maxW + 'px';

      let idx = 0;

      function swap() {
        idx = (idx + 1) % words.length;
        const next = words[idx];

        /* Fade current word out upward */
        current.classList.add('ht-word-out');

        setTimeout(function() {
          current.textContent = next;
          current.classList.remove('ht-word-out');
          current.classList.add('ht-word-in');

          /* Force reflow so transition fires */
          void current.offsetWidth;
          current.classList.remove('ht-word-in');
        }, TRANSITION);
      }

      const timer = setInterval(swap, interval);

      /* Expose stop for section unload */
      el._stopWordCarousel = function() { clearInterval(timer); };
    }

    function init() {
      document.querySelectorAll('.hero-ticker__word-carousel').forEach(startCarousel);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    document.addEventListener('shopify:section:load', function(e) {
      e.target.querySelectorAll('.hero-ticker__word-carousel').forEach(startCarousel);
    });

    document.addEventListener('shopify:section:unload', function(e) {
      e.target.querySelectorAll('.hero-ticker__word-carousel').forEach(function(el) {
        if (el._stopWordCarousel) el._stopWordCarousel();
      });
    });
  })();
})();
